import * as React from 'react';
import * as types from 'modules/types/smSupplierManagement';
import { QuerySelectorHTMLElement } from 'modules/types/commons/dom';
import { UploadFile, CertificateAttachment } from 'modules/types/commons/file';
import { Upload, Card } from '@beehive/components';
import _ from 'lodash';
import { easeSrcollTo } from 'utils/scroll';
import { beforeUploadSuccess } from 'utils/common';
import PropTypes from 'prop-types';
import globalMessages from 'messages';
import pageMessages from 'messages/CompanyManagement/QualifiedFile';
import titleMessages from 'messages/CompanyManagement/CardTitle';

const messages = { ...globalMessages, ...pageMessages, ...titleMessages };

const MAX_SIZE_LIMIT = 1024 * 1024 * 200;

export interface QualifiedFileProps {
  data: types.SupplierMangementDetail;
  onChange: any;
  supplierId?: number;
  showAlert: any;
  uploadModel?: string;
  getUploadUrl: () => string;
}

class QualifiedFile extends React.Component<QualifiedFileProps, any> {
  public static contextTypes = {
    intl: PropTypes.object,
  };

  public static defaultProps = {
    uploadModel: 'Supplier',
  };

  constructor(props: QualifiedFileProps) {
    super(props);
    this.state = {
      defaultFileList: this.getFileList(props),
    };
  }

  public componentDidMount() {
    if (window !== undefined) {
      window.addEventListener('resize', this.handleResize);
    }
  }

  public componentDidUpdate() {
    this.handleResize();
  }

  public componentWillReceiveProps(nextProps: QualifiedFileProps) {
    const pickKeys = ['supplierId', 'data.loading'];
    if (!_.isEqual(_.pick(this.props, pickKeys), _.pick(nextProps, pickKeys))) {
      this.setState({
        defaultFileList: this.getFileList(nextProps),
      });
    }
  }

  public componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
  }

  private handleBeforeUpload = (file: any, fileList: any) => {
    const { intl } = this.context;
    if (file.size && file.size > MAX_SIZE_LIMIT) {
      this.props.showAlert({
        type: 'error', message: intl.formatMessage(messages.fileExceededLimit, {
          filename: file.name, fileMaxSize: '200M',
        }),
      });
      return false;
    }
    return true;
  }

  private handleChange = (options: any) => {
    const { file } = options;
    const { onChange, data } = this.props;
    const { supplier_attaches = [] } = data;
    const newAttachs = [...supplier_attaches];
    switch (file.status) {
      case 'done':
        const { response: { data: res } } = file;
        newAttachs.push({
          status: 'added',
          cert_attach: {
            uid: file.uid,
            blob: {
              id: res.blob_id,
              filename: res.filename,
              byte_size: res.size,
              blob_key: res.blob_key,
            },
          },
        });
        this.handleScroll();
        break;
      case 'removed':
        newAttachs.forEach((a: CertificateAttachment) => {
          if (a.cert_attach.uid === file.uid || a.cert_attach.blob.id === file.uid) {
            a.status = 'removed';
          }
        });
        break;
      default:
        return;
    }
    const newData = {
      ...data,
      supplier_attaches: newAttachs,
    };
    onChange(newData);
  }

  private handleResize = () => {
    const upLoadListElement: QuerySelectorHTMLElement = document.querySelector('.qualified-file .beehive-upload-list');
    const basicInfoElement: QuerySelectorHTMLElement = document.querySelector('.basic-info');
    if (upLoadListElement && basicInfoElement) {
      // 24是底部padding的高度
      const scrollHeight = basicInfoElement.getBoundingClientRect().height - upLoadListElement.offsetTop - 24;
      upLoadListElement.style.height = `${scrollHeight}px`;
    }
  }

  private getUploadBody = (file: UploadFile) => {
    const { supplierId, uploadModel } = this.props;
    const body = {
      model: uploadModel,
      filename: file.name,
      attach_type: 'cert_attach',
    };
    if (supplierId) {
      body['model_id'] = supplierId;
    }
    return body;
  }

  private getFileList = (props: QualifiedFileProps) => {
    const { data: { supplier_attaches = [] } } = props;
    return supplier_attaches
      .filter((a: CertificateAttachment) => a.status !== 'removed')
      .map((a: CertificateAttachment) => {
        const { cert_attach: { blob: { id, filename, byte_size } } } = a;
        return {
          size: byte_size,
          uid: id,
          name: filename,
          status: a.status,
        };
      });
  }

  private handleScroll = () => {
    const elements = document.querySelectorAll('.upload-with-scroll .beehive-upload-list');
    if (elements.length) {
      const ele = elements[0];
      if (ele.scrollHeight > ele.clientHeight) {
        const to = ele.scrollHeight - ele.clientHeight - ele.scrollTop;
        easeSrcollTo({
          elementSelector: '.upload-with-scroll .beehive-upload-list',
          scrollAmount: to,
        });
      }
    }
  }

  public render() {
    const { intl } = this.context;
    const { data: { loading }, getUploadUrl } = this.props;
    const { defaultFileList } = this.state;
    return (
      <Card
        titleNode={intl.formatMessage(messages.qualifiedFile)}
        loading={loading}
        className="qualified-file"
      >
        <div className="upload-desc">
          {intl.formatMessage(messages.uploadDesc)}
          <br />
          <span>{intl.formatMessage(messages.uploadDescTip)}</span>
        </div>
        {!loading && <Upload
          beforeUpload={this.handleBeforeUpload}
          beforeSuccess={beforeUploadSuccess}
          className="upload-with-scroll"
          action={getUploadUrl()}
          data={this.getUploadBody}
          defaultFileList={defaultFileList}
          showUploadList={{ showRemoveIcon: true }}
          multiple
          uploadTips={intl.formatMessage(messages.uploadTip)}
          onChange={this.handleChange}
        />}
      </Card>
    );
  }
}

export default QualifiedFile;
